//
// state.ts — the single source of truth for "what's true right now"
//
// Translation of `ViewModels/AppViewModel.swift`. Same job: which layers
// are on, what instant the app is looking at, what is selected, the live
// clock, the current sun/moon positions. Every piece of the UI reads from
// this one object, and nothing else keeps a competing copy.
//
// Swift gets change notification free from `@Observable`. Here it is a
// plain subscribe/notify list — deliberately small, because the alternative
// (a framework) would put a dependency between Dave's design and the
// screen for no benefit this app can use. The renderer redraws on animation
// frames regardless; only the DOM chrome needs telling.
//

import type { LayerState, Place, TrackedObject } from "./types";
import { defaultLayerState } from "./types";
import * as Solar from "./orbital/solar";
import { fetchFlights, fetchMilitary } from "./api/aircraft";
import { fetchDefaultConstellations } from "./api/celestrak";
import { fetchNEOs } from "./api/neo";
import { fetchVessels } from "./api/vessels";
import { fetchPhoto } from "./api/photos";
import { fetchERA5, raster, type ERA5Grid } from "./api/era5";
import { unproject } from "./map/gleason";
import { placeToTrackedObject, searchPlaces } from "./api/places";

/**
 * Playback step → how many real seconds of past pass per real second.
 * Index 0 is live (rate unused). Owner 2026-08-22: 1, 2, then 5×.
 */
export const PLAYBACK_RATES = [0, 1, 2, 5] as const;

/** Ticked this often so reverse-play is smooth rather than stepped. */
export const TIMELINE_TICK_MS = 100;

/** How far back the timeline reaches. Unchanged. */
export const TIMELINE_MIN_HOURS = -6;

export const MIN_ZOOM = 0.85;
export const MAX_ZOOM = 8.0;

export type DockSide = "left" | "right";

type Listener = () => void;

export class AppState {
  // --- Layers & objects -------------------------------------------------
  layers: LayerState = defaultLayerState();
  objects: TrackedObject[] = [];
  selectedObject: TrackedObject | null = null;

  // --- Timeline & zoom --------------------------------------------------
  /**
   * Playback is not polarity. −3…−1 reverse (1×/2×/5×), 0 stopped,
   * +1…+3 forward toward now. Two buttons beside the polarity control.
   */
  playbackStep = 0;
  timeOffsetHours = 0;
  zoomScale = 1.0;
  /** True while a pointer is on the timeline ring, so play does not
   *  fight the scrub. */
  timelineHeld = false;

  /**
   * 0 = the outer lane is visibility. 1 = it is the global timeline.
   * Independent of playback.
   */
  polarityOn = false;

  /**
   * How far the disc is shoved from the centre of the view, as a fraction
   * of the map's radius. Build 1.5, item 8 — the arc's inner lane is a pan
   * control and until now there was nothing for it to move.
   *
   * **Pan only means anything while zoomed in.** Dave: *"If the map is
   * zoomed out all the way, pan will not work."* At 1.0 the whole disc
   * already fits, so sliding it would only push the Earth off the side of
   * a view with empty space in it. `canPan` is the one place that rule
   * lives.
   */
  panX = 0;
  panY = 0;

  get canPan(): boolean {
    return this.zoomScale > 1.0;
  }

  /** Pulls an existing pan back inside the limit after a zoom change. */
  clampPan(): void {
    const limit = Math.max(0, this.zoomScale - 1);
    this.panX = Math.min(limit, Math.max(-limit, this.panX));
    this.panY = Math.min(limit, Math.max(-limit, this.panY));
  }

  /** Clamped so the disc can never be shoved entirely out of view. */
  panBy(dx: number, dy: number): void {
    if (!this.canPan) return;
    const limit = this.zoomScale - 1;
    this.panX = Math.min(limit, Math.max(-limit, this.panX + dx));
    this.panY = Math.min(limit, Math.max(-limit, this.panY + dy));
    this.notify();
  }

  // --- Chrome -----------------------------------------------------------
  utcTimeString = "";
  isAiOpen = false;
  isLegendOpen = false;

  // --- Feed status ------------------------------------------------------
  isRefreshing = false;
  lastTelemetryRefresh: number | null = null;

  // --- Dial -------------------------------------------------------------
  /**
   * Which side the telemetry column and the arc menu sit on. Item 10 — one
   * setting moves both, so they are never on opposite sides of the screen.
   * The arc owns the stored preference; this is the live copy the renderer
   * and chrome read.
   */
  dockSide: DockSide = "right";
  isSettingsOpen = false;

  era5: ERA5Grid | null = null;
  era5Temp: HTMLCanvasElement | null = null;
  era5Cloud: HTMLCanvasElement | null = null;
  era5Precip: HTMLCanvasElement | null = null;

  /** Transient "[LABEL] — ON/OFF" readout. Null when idle. */
  hudFeedbackMessage: string | null = null;
  private hudFeedbackTimer: number | null = null;

  // --- Places -----------------------------------------------------------
  pinnedPlaces: Place[] = [];
  placeSearchQuery = "";
  placeSearchResults: Place[] = [];
  isPlaceSearchOpen = false;
  isSearchingPlaces = false;

  // --- Solar ------------------------------------------------------------
  subsolarPoint = Solar.subsolarPoint();
  sublunarPoint = Solar.sublunarPoint();

  private listeners: Listener[] = [];

  subscribe(fn: Listener): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }

  notify(): void {
    for (const fn of this.listeners) fn();
  }

  /**
   * The instant the app is visualising — "now" shifted by the timeline
   * scrubber. Mirrors `currentComputationTime`.
   */
  get currentComputationTimeMs(): number {
    return Date.now() + this.timeOffsetHours * 3600 * 1000;
  }

  /**
   * Sun and Moon subpoints as map objects, so they select and inspect the
   * same way satellites do. Mirrors `solarSystemObjects`.
   */
  get solarSystemObjects(): TrackedObject[] {
    return [
      {
        id: "subsolar-point",
        name: "Sub-Solar Point",
        category: "subsolar",
        typeLabel: "SUN — POINT OF ZENITH OVERHEAD",
        latitude: this.subsolarPoint.latitude,
        longitude: this.subsolarPoint.longitude,
        dataSource: "On-device solar ephemeris",
        lastUpdated: "Live",
      },
      {
        id: "sublunar-point",
        name: "Sub-Lunar Point",
        category: "sublunar",
        typeLabel: "MOON — POINT OF ZENITH OVERHEAD",
        latitude: this.sublunarPoint.latitude,
        longitude: this.sublunarPoint.longitude,
        dataSource: "On-device lunar ephemeris",
        lastUpdated: "Live",
      },
    ];
  }

  // --- Lifecycle loops --------------------------------------------------

  /** Live UTC clock plus solar refresh, once per second. */
  startClockLoop(): void {
    const tick = () => {
      const now = new Date();
      this.utcTimeString = now.toISOString().slice(0, 19).replace("T", " ");
      const t = this.currentComputationTimeMs;
      this.subsolarPoint = Solar.subsolarPoint(t);
      this.sublunarPoint = Solar.sublunarPoint(t);
      this.notify();
    };
    tick();
    window.setInterval(tick, 1000);
  }

  /**
   * Auto-advancing timeline. Must stay identical to
   * `AppViewModel.runTimelineLoop()`.
   *
   * Left button runs backward into known past; right runs forward toward
   * now. Polarity is a separate 0/1 and is not involved.
   */
  startTimelineLoop(): void {
    window.setInterval(() => {
      const mag = Math.min(Math.abs(this.playbackStep), 3);
      const rate = PLAYBACK_RATES[mag];
      if (rate > 0 && !this.timelineHeld) {
        const hoursPerTick = (rate * (TIMELINE_TICK_MS / 1000)) / 3600;
        if (this.playbackStep < 0) {
          this.timeOffsetHours = Math.max(
            TIMELINE_MIN_HOURS,
            this.timeOffsetHours - hoursPerTick,
          );
        } else {
          const next = this.timeOffsetHours + hoursPerTick;
          if (next >= 0) {
            this.timeOffsetHours = 0;
            this.playbackStep = 0;
          } else {
            this.timeOffsetHours = next;
          }
        }
        this.notify();
      }
    }, TIMELINE_TICK_MS);
  }

  /** Left playback button — rewind. 0 → −1 → −2 → −3 (1×, 2×, 5×). */
  tapReverse(): void {
    if (this.playbackStep > 0) this.playbackStep = -1;
    else this.playbackStep = Math.max(-3, this.playbackStep - 1);
    const rate = PLAYBACK_RATES[Math.abs(this.playbackStep)];
    this.showHUDFeedback(`[BACK ${rate}×]`);
    this.notify();
  }

  /** Right playback button — toward now. */
  tapForward(): void {
    if (this.playbackStep < 0) {
      this.playbackStep += 1;
      if (this.playbackStep === 0) {
        this.timeOffsetHours = 0;
        this.showHUDFeedback("[NOW]");
      } else {
        const rate = PLAYBACK_RATES[Math.abs(this.playbackStep)];
        this.showHUDFeedback(`[BACK ${rate}×]`);
      }
    } else if (this.timeOffsetHours < 0) {
      this.playbackStep = Math.min(3, this.playbackStep + 1);
      const rate = PLAYBACK_RATES[this.playbackStep];
      this.showHUDFeedback(`[FWD ${rate}×]`);
    } else {
      this.playbackStep = 0;
      this.timeOffsetHours = 0;
      this.showHUDFeedback("[NOW]");
    }
    this.notify();
  }

  /**
   * Scrubbing back from now re-arms play; the old loop could not be
   * restarted once it had stopped at zero.
   */
  setTimeOffsetHours(hours: number): void {
    this.timeOffsetHours = Math.min(0, Math.max(TIMELINE_MIN_HOURS, hours));
    this.notify();
  }

  /**
   * Aircraft positions go stale in seconds; element sets do not. Re-polling
   * ADS-B on its own cadence keeps the traffic picture current without
   * re-fetching satellites, which CelesTrak asks clients not to do more
   * than a couple of times a day.
   */
  startAircraftRefreshLoop(intervalSeconds = 45): void {
    window.setInterval(() => void this.refreshAircraft(), intervalSeconds * 1000);
  }

  // --- Telemetry --------------------------------------------------------

  /**
   * Loads every feed, drawing each one the moment it lands.
   *
   * This was a single `Promise.all` and it was wrong in a way that only
   * showed up live: the map stayed completely empty until the *slowest*
   * feed finished. Once satellites started coming from CelesTrak's real
   * catalogue — Starlink alone is eleven thousand records, several
   * megabytes — that could be twenty seconds of blank screen with working
   * aircraft data already sitting in memory, unused.
   *
   * Now each feed replaces its own contacts as it arrives, so the
   * instrument fills in: aircraft first, usually, then asteroids, then the
   * constellations. It also means a feed that fails or hangs costs only its
   * own layer instead of the whole picture.
   */
  async loadTelemetry(): Promise<void> {
    this.isRefreshing = true;
    this.notify();

    const jobs: Array<Promise<unknown>> = [
      fetchFlights().then((o) => this.mergeCategories(o, ["flight"])),
      fetchMilitary().then((o) => this.mergeCategories(o, ["military_aircraft"])),
      fetchVessels().then((o) => this.mergeCategories(o, ["vessel"])),
      fetchNEOs().then((o) => this.mergeCategories(o, ["neo_hazardous", "neo_safe"])),
      fetchDefaultConstellations().then((o) =>
        this.mergeCategories(o, ["starlink", "active", "debris", "rocket_body"]),
      ),
      fetchERA5().then((grid) => {
        if (!grid) return;
        this.era5 = grid;
        this.era5Temp = raster(grid, "temp", unproject);
        this.era5Cloud = raster(grid, "cloud", unproject);
        this.era5Precip = raster(grid, "precip", unproject);
        this.notify();
      }),
    ];

    // `allSettled`, not `all`: one rejected feed must not abandon the rest.
    await Promise.allSettled(jobs);

    this.lastTelemetryRefresh = Date.now();
    this.isRefreshing = false;
    this.notify();
  }

  /**
   * Swaps in a freshly-fetched set of contacts, replacing exactly the
   * categories that fetch is responsible for and leaving every other layer
   * untouched. An empty result is ignored rather than applied — a feed that
   * failed should leave the last good data on the map, not clear it.
   */
  private mergeCategories(
    incoming: TrackedObject[],
    categories: TrackedObject["category"][],
  ): void {
    if (incoming.length === 0) return;
    const owned = new Set<string>(categories);
    let batch = incoming;
    const liveStarlink = batch.some((o) => o.category === "starlink" && o.elements);
    if (liveStarlink) {
      batch = batch.filter((o) => !(o.category === "starlink" && !o.elements));
    }
    const seenNorad = new Set<number>();
    batch = batch.filter((o) => {
      if (o.noradId == null) return true;
      if (seenNorad.has(o.noradId)) return false;
      seenNorad.add(o.noradId);
      return true;
    });
    this.objects = [...this.objects.filter((o) => !owned.has(o.category)), ...batch];

    // **No auto-selection** — build 1.5, item 1.
    //
    // This used to select `objects[0]` so the viewer "had something
    // selected rather than hunting for their first click". That made sense
    // when every layer shipped on. With every contact layer now off by
    // default it is actively wrong: whichever feed happened to land first
    // got its first contact splashed across the telemetry panel while
    // *nothing was drawn on the map*, so the panel described something the
    // viewer could not see.
    //
    // In practice the winner was almost always EVER GIVEN — the first of
    // the six invented ships — which put a fabricated vessel in the most
    // prominent panel on a cold open. See `contracts/agent-conduct.md` #3.
    this.notify();
  }

  async refreshAircraft(): Promise<void> {
    const [aircraft, military] = await Promise.all([fetchFlights(), fetchMilitary()]);
    const fresh = [...aircraft, ...military];
    if (fresh.length === 0) return;

    // Replace only the aircraft — satellites, NEOs, vessels and places are
    // on their own cadences.
    this.mergeCategories(fresh, ["flight", "military_aircraft"]);
    this.lastTelemetryRefresh = Date.now();

    // If the selected aircraft got a fresher fix, follow it — carrying the
    // already-fetched photo across so the picture doesn't flicker away and
    // get re-requested every 45 seconds.
    const selected = this.selectedObject;
    if (selected && (selected.category === "flight" || selected.category === "military_aircraft")) {
      const updated = fresh.find((o) => o.id === selected.id);
      if (updated) {
        this.selectedObject = {
          ...updated,
          photoUrl: selected.photoUrl,
          photoPageUrl: selected.photoPageUrl,
          photoCredit: selected.photoCredit,
        };
      }
    }
    this.notify();
  }

  // --- User actions -----------------------------------------------------

  selectObject(obj: TrackedObject | null): void {
    this.selectedObject = obj;
    this.notify();

    // Photograph lookups happen here, on selection, never in bulk:
    // Planespotters allows about one request a second and the map can be
    // carrying four hundred aircraft.
    if (!obj || obj.photoUrl) return;
    if (obj.category !== "flight" && obj.category !== "military_aircraft") return;

    void fetchPhoto(obj.registration, obj.icao24).then((photo) => {
      if (!photo) return;
      // Only apply if the user hasn't moved on while it was in flight.
      if (this.selectedObject?.id !== obj.id) return;
      this.selectedObject = {
        ...this.selectedObject,
        photoUrl: photo.imageUrl,
        photoPageUrl: photo.pageUrl,
        photoCredit: photo.photographer,
      };
      this.notify();
    });
  }

  centerOnObject(obj: TrackedObject): void {
    this.selectObject(obj);
    this.zoomScale = 1.25;
    this.notify();
  }

  zoomIn(): void {
    this.zoomScale = Math.min(MAX_ZOOM, this.zoomScale * 1.15);
    this.notify();
  }

  zoomOut(): void {
    this.zoomScale = Math.max(MIN_ZOOM, this.zoomScale / 1.15);
    this.clampPan();
    this.notify();
  }

  resetZoom(): void {
    this.zoomScale = 1.0;
    this.panX = 0;
    this.panY = 0;
    this.notify();
  }

  showHUDFeedback(message: string, seconds = 1.6): void {
    this.hudFeedbackMessage = message;
    if (this.hudFeedbackTimer !== null) window.clearTimeout(this.hudFeedbackTimer);
    this.hudFeedbackTimer = window.setTimeout(() => {
      this.hudFeedbackMessage = null;
      this.notify();
    }, seconds * 1000);
    this.notify();
  }

  // --- Place search -----------------------------------------------------

  async runPlaceSearch(): Promise<void> {
    const query = this.placeSearchQuery;
    if (query.trim().length < 2) {
      this.placeSearchResults = [];
      this.notify();
      return;
    }
    this.isSearchingPlaces = true;
    this.notify();

    const results = await searchPlaces(query);
    // A newer keystroke may have superseded this search.
    if (this.placeSearchQuery !== query) return;
    this.placeSearchResults = results;
    this.isSearchingPlaces = false;
    this.notify();
  }

  pinPlace(place: Place): void {
    if (this.pinnedPlaces.some((p) => p.id === place.id)) return;
    this.pinnedPlaces.push(place);
    const obj = placeToTrackedObject(place);
    this.objects.push(obj);
    this.selectObject(obj);
    this.showHUDFeedback(`[${place.name.toUpperCase()}] PINNED`);
  }

  unpinPlace(place: Place): void {
    this.pinnedPlaces = this.pinnedPlaces.filter((p) => p.id !== place.id);
    this.objects = this.objects.filter((o) => o.id !== `place-${place.id}`);
    if (this.selectedObject?.id === `place-${place.id}`) this.selectedObject = null;
    this.notify();
  }

  togglePlaceSearch(): void {
    this.isPlaceSearchOpen = !this.isPlaceSearchOpen;
    if (!this.isPlaceSearchOpen) {
      this.placeSearchQuery = "";
      this.placeSearchResults = [];
    }
    this.notify();
  }
}
